import {
  CHAT_TYPE,
  GROUP_TYPE,
  IBroadcastChats,
  IBroadcastInfo,
  IGroupInfo,
  INonPatientChats,
  IPatientChats,
} from "@home-health-notify/domain";
import {
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { ConfirmationDialog } from "../../../common/ConfirmationDialog";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { ROUTES } from "../../../routes";
import DeleteButton from "./DeleteButton";
import { DeleteGroupWrapper } from "./Settings.style";

type DeleteGroupType = {
  groupInfo: IGroupInfo | IBroadcastInfo;
};

export const DeleteGroup = ({ groupInfo }: DeleteGroupType) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const tNsDrawer = (key: string) => t(key, { ns: "drawerHeader" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { closeDrawer } = useContext(RightDrawerContext);

  const isBroadcastGroup = groupInfo.groupType === GROUP_TYPE.BROADCAST;

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const openDeleteConfirmationDialog = () => {
    setIsConfirmationDialogOpen(true);
  };

  const onCloseConfirmationDialog = () => {
    setIsConfirmationDialogOpen(false);
  };

  const onDeleteGroup = async () => {
    try {
      const successMessage = `${groupInfo.name} ${
        isBroadcastGroup
          ? tNsDrawer("deleteBroadcastSuccessMessage")
          : tNsDrawer("deleteGroupSuccessMessage")
      }`;

      const chatManager =
        selectedOrgUser?.chatManager.getChatManagerForGroupType(
          groupInfo.groupType,
        ) as IPatientChats | INonPatientChats | IBroadcastChats;

      if (isBroadcastGroup) {
        await (chatManager as IBroadcastChats).deleteChat(groupInfo.id);
      } else {
        await (chatManager as IPatientChats | INonPatientChats).deleteChat({
          id: groupInfo.id,
          chatType: CHAT_TYPE.GROUP,
        });
      }

      enqueueSnackbar(successMessage, {
        autoHideDuration: 3000,
      });
      const orgId = selectedOrgUser?.getOrganization().organizationId;
      navigate(`${ROUTES.ORGANIZATIONS}/${orgId}/${ROUTES.CHATS}`);
      closeDrawer();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  return (
    <DeleteGroupWrapper data-testid="delete-group">
      <DeleteButton
        groupType={groupInfo.groupType}
        onDelete={openDeleteConfirmationDialog}
      />
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={onCloseConfirmationDialog}
        confirmationTitle={
          isBroadcastGroup ? tNs("deleteBroadcast") : tNs("deleteGroup")
        }
        confirmationMessage={
          isBroadcastGroup
            ? tNs("confirmDeleteBroadcast")
            : tNs("confirmDeleteGroup")
        }
        confirmationButtonText={tNs("delete")}
        onConfirmationAction={onDeleteGroup}
      />
    </DeleteGroupWrapper>
  );
};
